"use client";
import { motion } from "framer-motion";
import {
    BeakerIcon,
    BoltIcon,
    HeartIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import FoodChoiceLottie from "../../lottiefiles/FoodChoice.json";
import { useSEO } from "../../hooks/useSEO";


// Services/Features data
const services = [
    {
        name: "Personalized Meal Planning",
        description:
            "Custom-tailored nutrition plans designed to meet your specific energy needs, training intensity, and performance goals.",
        icon: SparklesIcon,
    },
    {
        name: "Performance Hydration",
        description:
            "Scientific hydration strategies to maintain peak performance and enhance recovery during intense training and competition.",
        icon: BoltIcon,
    },
    {
        name: "Weight Management",
        description:
            "Evidence-based strategies for achieving and maintaining optimal body composition for your specific sport.",
        icon: HeartIcon, // Using HeartIcon as a proxy for health/weight
    },
    {
        name: "Clinical Nutrition",
        description:
            "Management of medical conditions and dietary intolerances to ensure your health never compromises your athletic potential.",
        icon: BeakerIcon,
    },
];

const benefits = [
    "Optimized energy levels",
    "Faster muscle recovery",
    "Reduced injury risk",
    "Improved mental focus",
    "Long-term health sustainability",
    "Immune system support",
];

export default function NutritionAndDietetics(): JSX.Element {
    useSEO({
        title: "Nutrition & Dietetics | Sports Performance Chennai",
        description: "Expert sports nutrition and dietetics to optimize your performance, recovery, and long-term health. Personalized meal planning and clinical nutrition.",
        keywords: "sports nutritionist chennai, sports dietetics anna nagar, athlete meal planning, dietary counseling"
    });

    return (
        <div className="bg-gray-50 text-gray-800">
            {/* Hero Section */}
            <section className="relative bg-indigo-600 text-white py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-50">
                    <img
                        src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
                        alt="Healthy food background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-indigo-900/40 mix-blend-multiply" />

                <motion.div
                    className="relative max-w-6xl mx-auto px-6 text-center z-10"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                        Fuel Your Ambition
                    </h1>
                    <p className="text-xl md:text-2xl max-w-3xl mx-auto text-indigo-50 font-light">
                        Expert sports nutrition and dietetics to optimize your performance, recovery, and long-term health.
                    </p>
                </motion.div>
            </section>

            <DotLottieReact
                data={FoodChoiceLottie}
                loop
                autoplay
            />

            {/* Introduction Feature */}
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-2xl lg:text-center">
                    <h2 className="text-base font-semibold leading-7 text-indigo-600">Science-Backed Approach</h2>
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Nutrition is your secret weapon
                    </p>
                    <p className="mt-6 text-lg leading-8 text-gray-600">
                        Our certified sports dietitians work directly with you to translate complex nutritional science into practical, sustainable habits that drive results on and off the field.
                    </p>
                </div>

                <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                    <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4">
                        {services.map((service) => (
                            <div key={service.name} className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                    <service.icon className="h-5 w-5 flex-none text-indigo-600" aria-hidden="true" />
                                    {service.name}
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                    <p className="flex-auto">{service.description}</p>
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>

            {/* Image & Benefits Split Section */}
            <div className="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 lg:overflow-visible lg:px-0 mt-12">
                <div className="absolute inset-0 -z-10 overflow-hidden">
                    <svg
                        className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
                        aria-hidden="true"
                    >
                        <defs>
                            <pattern
                                id="e813992c-7d03-4cc4-a2bd-151760b470a0"
                                width={200}
                                height={200}
                                x="50%"
                                y={-1}
                                patternUnits="userSpaceOnUse"
                            >
                                <path d="M100 200V.5M.5 .5H200" fill="none" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" strokeWidth={0} fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)" />
                    </svg>
                </div>

                <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10">
                    <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
                        <div className="lg:pr-4">
                            <div className="lg:max-w-lg">
                                <p className="text-base font-semibold leading-7 text-indigo-600">Holistic Care</p>
                                <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                    Eat better, perform better
                                </h1>
                                <p className="mt-6 text-xl leading-8 text-gray-700">
                                    We believe that food is information for your cells. The right nutrition strategy can be the difference between a good season and a record-breaking one.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="-ml-12 -mt-12 p-12 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:overflow-hidden">
                        <img
                            className="w-[48rem] max-w-none rounded-xl bg-gray-900 shadow-xl ring-1 ring-gray-400/10 sm:w-[57rem]"
                            src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
                            alt="Healthy meal preparation"
                        />
                    </div>

                    <div className="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
                        <div className="lg:pr-4">
                            <div className="max-w-xl text-base leading-7 text-gray-700 lg:max-w-lg">
                                <p>
                                    Our sports dietitians collaborate closely with your medical team to ensure your nutritional intake aligns with your rehabilitation and training goals.
                                </p>
                                <ul role="list" className="mt-8 space-y-8 text-gray-600">
                                    {benefits.map((benefit, index) => (
                                        <li key={index} className="flex gap-x-3">
                                            <SparklesIcon className="mt-1 h-5 w-5 flex-none text-indigo-600" aria-hidden="true" />
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-8">
                                    Whether you're recovering from injury or preparing for the championships, our team is here to guide your nutritional choices every step of the way.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Simple CTA */}
            <div className="bg-indigo-50 py-16 sm:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Ready to optimize your nutrition?
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-600">
                            Book a consultation with one of our specialized sports nutritionists today.
                        </p>
                        <div className="mt-10 flex items-center justify-center gap-x-6">
                            <a
                                href="/appointment"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            >
                                Book an Appointment
                            </a>
                            <a href="/about#team" className="text-sm font-semibold leading-6 text-gray-900">
                                Meet the Team <span aria-hidden="true">→</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
